import { useState } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { isBackofficeConnected, loginBackoffice, logoutBackoffice } from '@/api/backofficeAuth';

interface BackofficeTokenDialogProps {
    open: boolean;
    onClose: () => void;
    onDisconnected?: () => void;
}

export function BackofficeTokenDialog({
    open,
    onClose,
    onDisconnected,
}: BackofficeTokenDialogProps) {
    const [value, setValue] = useState('');
    const [invalid, setInvalid] = useState(false);
    const [pending, setPending] = useState(false);

    function handleClose() {
        setValue('');
        setInvalid(false);
        onClose();
    }

    async function handleSave() {
        setPending(true);
        const ok = await loginBackoffice(value.trim());
        setPending(false);
        if (!ok) {
            setInvalid(true);
            return;
        }
        handleClose();
    }

    async function handleDisconnect() {
        setPending(true);
        await logoutBackoffice();
        setPending(false);
        handleClose();
        onDisconnected?.();
    }

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Backoffice token</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Exchanged once for an httpOnly cookie the hub sets — never stored or read back
                    by this page. {isBackofficeConnected() ? 'Currently connected.' : ''} Left
                    empty, the hub accepts requests unauthenticated (its local-dev default).
                </DialogContentText>
                <TextField
                    autoFocus
                    fullWidth
                    label="BACKOFFICE_TOKEN"
                    type="password"
                    value={value}
                    onChange={(event) => {
                        setValue(event.target.value);
                        setInvalid(false);
                    }}
                    error={invalid}
                    helperText={invalid ? 'Invalid token' : ' '}
                />
            </DialogContent>
            <DialogActions>
                {isBackofficeConnected() && (
                    <Button onClick={handleDisconnect} color="error" disabled={pending}>
                        Disconnect
                    </Button>
                )}
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={pending || !value.trim()}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
